'use client'

import { m, useInView, useReducedMotion } from 'motion/react'
import { useRef } from 'react'

interface Primitive {
    character: string
    meaning: string
}
interface Composed {
    character: string
    meaning: string
    frameNumber: number
}

/**
 * Animated composition diagram.
 *
 * Primitives ease in from the side, the vermilion arrow draws across,
 * the composed glyph stamps in. The motion enacts the Heisig
 * composition idea: separate parts → unified meaning. The "+" is
 * structural punctuation, not eventful — kept static.
 *
 * Vermilion appears here only on the connecting arrow — the chromatic
 * "moment of synthesis" anchor (one of the §4.5 budget placements).
 */
export function CompositionDiagram({
    primitives,
    composed,
}: {
    primitives: Primitive[]
    composed: Composed
}) {
    const reduce = useReducedMotion()
    const ref = useRef<HTMLDivElement | null>(null)
    const inView = useInView(ref, { once: true, margin: '0px 0px -10% 0px' })

    const animate = inView && !reduce
    const [p1, p2] = primitives

    return (
        <div
            ref={ref}
            className="grid grid-cols-1 items-center justify-items-center gap-y-6 border-b border-ink/15 px-6 py-10 md:grid-cols-[1fr_auto_1fr_auto_1fr] md:gap-y-0 md:px-8 md:py-14"
            aria-label={`${p1?.character} plus ${p2?.character} equals ${composed.character}`}
        >
            <PrimitiveCell prim={p1} animate={animate} delay={0} from="left" />

            <span
                aria-hidden
                className="my-3 font-serif text-[28px] leading-none text-muted-fg md:mx-2 md:my-0"
            >
                +
            </span>

            <PrimitiveCell
                prim={p2}
                animate={animate}
                delay={0.06}
                from="left"
            />

            <Arrow animate={animate} delay={0.36} />

            <ComposedCell composed={composed} animate={animate} delay={0.5} />
        </div>
    )
}

function PrimitiveCell({
    prim,
    animate,
    delay,
    from,
}: {
    prim: Primitive | undefined
    animate: boolean
    delay: number
    from: 'left' | 'right'
}) {
    if (!prim) return <div />
    return (
        <m.div
            initial={
                animate ? { opacity: 0, x: from === 'left' ? -16 : 16 } : false
            }
            animate={animate ? { opacity: 1, x: 0 } : undefined}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1], delay }}
            className="flex flex-col items-center text-center"
        >
            <div className="mb-2 font-mincho text-[64px] leading-none text-ink md:text-[72px]">
                {prim.character}
            </div>
            <div className="font-serif text-[10px] tracking-[0.22em] text-muted-fg uppercase">
                {prim.meaning}
            </div>
        </m.div>
    )
}

function Arrow({ animate, delay }: { animate: boolean; delay: number }) {
    return (
        <div className="my-2 inline-flex rotate-90 md:mx-3 md:my-0 md:rotate-0">
            <m.div
                aria-hidden
                initial={animate ? { opacity: 0, scaleX: 0 } : false}
                animate={animate ? { opacity: 1, scaleX: 1 } : undefined}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1], delay }}
                className="flex origin-left items-center"
            >
                <span className="h-[1.5px] w-12 bg-vermilion md:w-16" />
                <span className="border-y-[5px] border-l-[8px] border-y-transparent border-l-vermilion" />
            </m.div>
        </div>
    )
}

function ComposedCell({
    composed,
    animate,
    delay,
}: {
    composed: Composed
    animate: boolean
    delay: number
}) {
    return (
        <m.div
            initial={animate ? { opacity: 0, scale: 0.85 } : false}
            animate={animate ? { opacity: 1, scale: 1 } : undefined}
            transition={{
                duration: 0.7,
                ease: [0.34, 1.56, 0.64, 1],
                delay,
            }}
            className="flex flex-col items-center text-center"
        >
            <div className="mb-2 font-mincho text-[96px] leading-none text-ink md:text-[112px]">
                {composed.character}
            </div>
            <div className="font-serif text-[18px] text-ink italic md:text-[20px]">
                {composed.meaning}
            </div>
            <div className="mt-1 font-serif text-[9px] tracking-[0.28em] text-muted-fg uppercase">
                Frame {composed.frameNumber}
            </div>
        </m.div>
    )
}
